import { FilterOutlined } from "@ant-design/icons";
import { Checkbox, Flex, Select } from "antd";
import { FC, useCallback, useMemo } from "react";
import {
    Market,
    SubtaskProductsMatrix,
    TaskAssortmentMatrix,
} from "tm-shared/types";

import { PrimaryText } from "@/components/Typography/Styled";
import { ForceStepComplete } from "@/features/ForceStepComplete";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setAssortmentMatrixSubtaskPayload } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";

import {
    getNextSubtaskId,
    makeForceStepCompletePayload,
} from "../../../AssortmentMatrix.utils";
import { StyledSelect } from "./Styled";

const { Option } = Select;

interface ProductsMatrixControllerProps {
    subtask: SubtaskProductsMatrix;
    market: Market;
    task: TaskAssortmentMatrix;
}

const PLACEHOLDER_TEXT = "Детальна група";
const LABEL_TEXT = "Показати невиконані";

export const ProductsMatrixController: FC<ProductsMatrixControllerProps> = ({
    subtask,
    market,
    task,
}) => {
    const dispatch = useAppDispatch();

    const marketId = market.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;

    // Селектори
    const tasksConfig = useAppSelector((state) => state.ui.mdTasksConfig);
    const { products } = useAppSelector((state) => state.offline);

    // Конфігурація поточної підзадачі
    const currentSubtaskConfig = useMemo(
        () => tasksConfig?.[marketId]?.[taskId]?.subtasks?.[subtaskId],
        [tasksConfig, marketId, taskId, subtaskId],
    );

    // Фінансові групи товарів
    const finGroupsFromProducts = useMemo(
        () =>
            subtask.context.products
                .map(
                    (barcode) =>
                        products[barcode]?.params?.fin_group_detail?.value,
                )
                .filter(Boolean), // Видаляємо undefined/null значення
        [products, subtask.context.products],
    );

    const uniqueFinGroups = useMemo(
        () => Array.from(new Set(finGroupsFromProducts)),
        [finGroupsFromProducts],
    );

    // Поточні значення фільтрів
    const selectedFinGroup = useMemo(
        () => currentSubtaskConfig?.chosenFingroup ?? null,
        [currentSubtaskConfig?.chosenFingroup],
    );

    const shouldShowUncompleted = useMemo(
        () => currentSubtaskConfig?.showUncompleted ?? false,
        [currentSubtaskConfig?.showUncompleted],
    );

    // Базові параметри для dispatch
    const baseTaskConfigParams = useMemo(
        () => ({
            marketId,
            taskId,
            subtaskId,
        }),
        [marketId, taskId, subtaskId],
    );

    // Функція для скидання активного товару
    const resetActiveProduct = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                ...baseTaskConfigParams,
                value: null,
            }),
        );
    }, [dispatch, baseTaskConfigParams]);

    // Обробники подій
    const handleFinGroupChange = useCallback(
        (selectedValue: string) => {
            const finGroupValue = selectedValue || null;

            // Встановлюємо нову фінансову групу
            dispatch(
                setMdTasksConfig({
                    ...baseTaskConfigParams,
                    field: "chosenFingroup",
                    value: finGroupValue,
                }),
            );

            // Скидаємо активний товар при зміні групи
            resetActiveProduct();
        },
        [dispatch, baseTaskConfigParams, resetActiveProduct],
    );

    const handleShowUncompletedChange = useCallback(
        (isChecked: boolean) => {
            // Встановлюємо прапорець показу невиконаних
            dispatch(
                setMdTasksConfig({
                    ...baseTaskConfigParams,
                    field: "showUncompleted",
                    value: isChecked,
                }),
            );

            // Скидаємо активний товар при зміні фільтра
            resetActiveProduct();
        },
        [dispatch, baseTaskConfigParams, resetActiveProduct],
    );

    // Placeholder для Select
    const selectPlaceholder = useMemo(
        () => (
            <>
                <FilterOutlined />
                &nbsp; {PLACEHOLDER_TEXT}
            </>
        ),
        [],
    );

    const handleForceStepComplete = useCallback(
        async (comment: string) => {
            const payload = makeForceStepCompletePayload({
                subtask,
                comment,
            });

            dispatch(
                setAssortmentMatrixSubtaskPayload({
                    taskId,
                    subtaskId,
                    payload,
                }),
            );

            const { nextSubtaskId } = getNextSubtaskId({
                subtasks: task.context.subtasks,
                currentSubtaskId: subtaskId,
            });

            if (nextSubtaskId) {
                dispatch(
                    setMdTasksConfig({
                        marketId,
                        taskId,
                        field: "activeItem",
                        value: nextSubtaskId,
                    }),
                );
            }
        },
        [dispatch, marketId, subtask, subtaskId, task.context.subtasks, taskId],
    );

    return (
        <Flex vertical gap={16}>
            <StyledSelect
                value={selectedFinGroup}
                onChange={handleFinGroupChange}
                listHeight={400}
                allowClear
                size="large"
                style={{ width: "100%" }}
                popupMatchSelectWidth={false}
                placeholder={selectPlaceholder}
            >
                {uniqueFinGroups.map((finGroup) => (
                    <Option key={finGroup} value={finGroup}>
                        {finGroup}
                    </Option>
                ))}
            </StyledSelect>

            <Flex align="center" justify="space-between" gap={8}>
                <Checkbox
                    checked={shouldShowUncompleted}
                    onChange={(event) =>
                        handleShowUncompletedChange(event.target.checked)
                    }
                >
                    <PrimaryText>{LABEL_TEXT}</PrimaryText>
                </Checkbox>

                <ForceStepComplete
                    onComplete={handleForceStepComplete}
                    taskName={task.name}
                    marketName={market.name}
                    entity="step"
                />
            </Flex>
        </Flex>
    );
};
